import {useEffect, useState} from 'react'
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import {
  ApplicationBypassData,
  ApplicationOSType,
  BypassDataClient,
  BypassType,
  DefaultBypassApiItem,
  DefaultBypassState,
  TrafficType,
  UrlBypassData,
} from 'interfaces'
import {PoliciesService} from 'services/api'
import {getApplicationBypassResourceQueryKey} from './useGetApplicationBypassResource'
import {getUrlBypassResourceQueryKey} from './useGetUrlBypassResource'

export const GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY =
  'getUrlAndApplicationBypassData'

interface GetUrlAndApplicationBypassQueryProps {
  policyName: string
  enabled?: boolean
}

interface BypassResourceData {
  applicationBypass: ApplicationBypassData
  urlBypass: UrlBypassData
}

export interface GetBypassQueryResponse
  extends Omit<UseQueryResult<BypassDataClient>, 'data'> {
  data: BypassDataClient
  onUpdateBypassData: (data: BypassDataClient) => void
}

export const createEmptyBypassData = (): BypassDataClient => ({
  customDomainBypassList: [],
  domainDefaultBypassList: [],
  domainIgnoreBypassList: [],
  customApplicationBypassList: [],
  applicationDefaultBypassList: [],
  applicationIgnoreBypassList: [],
  inheritsApplicationBypass: false,
  inheritsDomainBypass: false,
})

interface BypassDataState {
  data: BypassDataClient
  ownerKey: string
}

const getBypassDataOwnerKey = (policyName: string, enabled: boolean): string =>
  enabled ? policyName : ''

export const useGetUrlAndApplicationBypass = ({
  policyName,
  enabled = true,
}: GetUrlAndApplicationBypassQueryProps): GetBypassQueryResponse => {
  const queryClient = useQueryClient()
  const bypassDataOwnerKey = getBypassDataOwnerKey(policyName, enabled)
  const [bypassDataState, setBypassDataState] = useState<BypassDataState>(
    () => ({
      data: createEmptyBypassData(),
      ownerKey: bypassDataOwnerKey,
    }),
  )

  const {data, isLoading, isSuccess, isError, error, ...rest} = useQuery({
    queryKey: [GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY, policyName],
    queryFn: async (): Promise<BypassResourceData> => {
      const [{data: urlBypass}, {data: applicationBypass}] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: getUrlBypassResourceQueryKey(policyName),
          queryFn: () => PoliciesService.getUrlBypass(policyName),
        }),
        queryClient.fetchQuery({
          queryKey: getApplicationBypassResourceQueryKey(policyName),
          queryFn: () => PoliciesService.getApplicationBypass(policyName),
        }),
      ])

      return {
        applicationBypass,
        urlBypass,
      }
    },
    enabled,
    select: transformBypassResourcesToClientData,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    setBypassDataState(currentState => {
      if (currentState.ownerKey === bypassDataOwnerKey) {
        return currentState
      }

      return {
        data: createEmptyBypassData(),
        ownerKey: bypassDataOwnerKey,
      }
    })
  }, [bypassDataOwnerKey])

  useEffect(() => {
    if (!enabled || isLoading || !data) {
      return
    }

    setBypassDataState({
      data,
      ownerKey: bypassDataOwnerKey,
    })
  }, [enabled, isLoading, data, bypassDataOwnerKey])

  const onUpdateBypassData = (updatedData: BypassDataClient): void =>
    setBypassDataState({
      data: updatedData,
      ownerKey: bypassDataOwnerKey,
    })

  const bypassData =
    bypassDataState.ownerKey === bypassDataOwnerKey
      ? bypassDataState.data
      : createEmptyBypassData()

  return {
    data: bypassData,
    onUpdateBypassData,
    isLoading,
    isSuccess,
    isError,
    error,
    ...rest,
  }
}

const toTrafficType = (state: DefaultBypassState): TrafficType =>
  state === DefaultBypassState.IGNORED ? TrafficType.IGNORE : TrafficType.BYPASS

const getIgnoredDefaultNames = (items: DefaultBypassApiItem[]): string[] =>
  items
    .filter(item => item.state === DefaultBypassState.IGNORED)
    .map(item => item.name)

const getUpdatedAtTime = (updatedAt?: string): number | undefined => {
  if (!updatedAt) return undefined

  const time = new Date(updatedAt).getTime()
  return Number.isNaN(time) ? undefined : time
}

const sortByUpdatedAtDesc = <T extends {updatedAt?: string}>(items: T[]): T[] =>
  items
    .map((item, index) => ({item, index}))
    .sort((a, b) => {
      const aUpdatedAtTime = getUpdatedAtTime(a.item.updatedAt)
      const bUpdatedAtTime = getUpdatedAtTime(b.item.updatedAt)

      if (aUpdatedAtTime === undefined && bUpdatedAtTime === undefined) {
        return a.index - b.index
      }
      if (aUpdatedAtTime === undefined) return 1
      if (bUpdatedAtTime === undefined) return -1

      const updatedAtDifference = bUpdatedAtTime - aUpdatedAtTime
      return updatedAtDifference || a.index - b.index
    })
    .map(({item}) => item)

export const transformUrlBypassToClientData = (
  urlBypass: UrlBypassData,
): BypassDataClient => ({
  customDomainBypassList: sortByUpdatedAtDesc(
    urlBypass.custom.map(item => ({
      ...item,
      isEdit: false,
      isDefault: false,
      bypassType: BypassType.url,
    })),
  ),
  domainDefaultBypassList: urlBypass.default.map(item => ({
    name: item.name,
    bypassType: BypassType.url,
    isDefault: true,
    traffic: toTrafficType(item.state),
    isEdit: false,
  })),
  domainIgnoreBypassList: getIgnoredDefaultNames(urlBypass.default),
  customApplicationBypassList: [],
  applicationDefaultBypassList: [],
  applicationIgnoreBypassList: [],
  inheritsApplicationBypass: false,
  inheritsDomainBypass: urlBypass.inheritsFromBase,
})

const transformBypassResourcesToClientData = ({
  applicationBypass,
  urlBypass,
}: BypassResourceData): BypassDataClient => {
  const ignoredMacApps = getIgnoredDefaultNames(applicationBypass.default.mac)
  const ignoredWindowsApps = getIgnoredDefaultNames(
    applicationBypass.default.windows,
  )
  const urlBypassData = transformUrlBypassToClientData(urlBypass)

  return {
    ...urlBypassData,
    customApplicationBypassList: sortByUpdatedAtDesc([
      ...applicationBypass.custom.mac.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
      })),
      ...applicationBypass.custom.windows.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
      })),
    ]),
    applicationDefaultBypassList: [
      ...applicationBypass.default.mac.map(item => ({
        name: item.name,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.MAC,
        traffic: toTrafficType(item.state),
        isEdit: false,
      })),
      ...applicationBypass.default.windows.map(item => ({
        name: item.name,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.WINDOWS,
        traffic: toTrafficType(item.state),
        isEdit: false,
      })),
    ],
    applicationIgnoreBypassList: [
      ...ignoredMacApps.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
        isDefault: false,
        isEdit: false,
      })),
      ...ignoredWindowsApps.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
        isDefault: false,
        isEdit: false,
      })),
    ],
    inheritsApplicationBypass: applicationBypass.inheritsFromBase,
  }
}
